import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Home, ChevronRight, Plus, Trash2, Upload, Download, Loader2, Save, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const emptyBank = () => ({ bank_name: '', account_no: '', branch: '', ifsc_code: '' });

const AddDriver = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: driverId } = useParams();
  const { toast } = useToast();
  const isEdit = !!driverId;
  const queryVendorId = new URLSearchParams(location.search).get('vendor_id') || '';

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [newDocName, setNewDocName] = useState('');
  const [newDocFile, setNewDocFile] = useState(null);
  const [bankRows, setBankRows] = useState([emptyBank()]);

  const [form, setForm] = useState({
    vendor_id: !isEdit && queryVendorId ? queryVendorId : '', name: '', aadhar_no: '', dl_no: '', dl_issued_by_state: '',
    dl_expiry_date: '', mobile_1: '', mobile_2: '',
    bhattu_local: '', bhattu_outstation: '', conductor_bhattu: '',
    date_of_joining: '', relieving_date: '', address: '', is_active: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchMasters = useCallback(async () => {
    const [vRes, drRes] = await Promise.all([
      fetch(`${backendUrl}/api/vendors`, { headers: getAuthHeaders() }),
      fetch(`${backendUrl}/api/drive/status`, { headers: getAuthHeaders() }).catch(() => null),
    ]);
    if (vRes.ok) { const v = await vRes.json(); setVendors(v.items || []); }
    if (drRes?.ok) { const d = await drRes.json(); setDriveConnected(d.connected); }
  }, [backendUrl, getAuthHeaders]);

  const fetchDriver = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/drivers/${driverId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Not found');
      const d = await res.json();
      setForm({
        vendor_id: d.vendor_id || '', name: d.name || '', aadhar_no: d.aadhar_no || '',
        dl_no: d.dl_no || '', dl_issued_by_state: d.dl_issued_by_state || '',
        dl_expiry_date: d.dl_expiry_date || '', mobile_1: d.mobile_1 || '',
        mobile_2: d.mobile_2 || '', bhattu_local: d.bhattu_local ?? '',
        bhattu_outstation: d.bhattu_outstation ?? '', conductor_bhattu: d.conductor_bhattu ?? '',
        date_of_joining: d.date_of_joining || '', relieving_date: d.relieving_date || '',
        address: d.address || '', is_active: d.is_active
      });
      setKycDocuments(d.kyc_documents || []);
      setBankRows(d.bank_details?.length > 0
        ? d.bank_details.map(b => ({ id: b.id, bank_name: b.bank_name, account_no: b.account_no, branch: b.branch || '', ifsc_code: b.ifsc_code || '' }))
        : [emptyBank()]
      );
    } catch { toast({ title: "Error", description: "Failed to load driver", variant: "destructive" }); navigate('/fleet/drivers'); }
    finally { setLoading(false); }
  }, [driverId, backendUrl, getAuthHeaders, toast, navigate]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);
  useEffect(() => { if (isEdit) fetchDriver(); }, [isEdit, fetchDriver]);
  useEffect(() => {
    if (!isEdit && queryVendorId) {
      setForm(prev => ({ ...prev, vendor_id: queryVendorId }));
    }
  }, [isEdit, queryVendorId]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // ---- Bank rows ----
  const addBankRow = () => setBankRows(p => [...p, emptyBank()]);
  const removeBankRow = (i) => setBankRows(p => p.filter((_, idx) => idx !== i));
  const setBankField = (i, f, v) => setBankRows(p => { const u = [...p]; u[i] = { ...u[i], [f]: v }; return u; });

  // ---- KYC upload ----
  const handleUploadDoc = async () => {
    if (!newDocName || !newDocFile) { toast({ title: "Error", description: "Enter document name and select file", variant: "destructive" }); return; }
    if (!driverId) { toast({ title: "Info", description: "Save driver first before uploading documents" }); return; }
    try {
      setUploadingDoc(true);
      const fd = new FormData(); fd.append('file', newDocFile);
      const res = await fetch(`${backendUrl}/api/drivers/${driverId}/kyc-documents?document_name=${encodeURIComponent(newDocName)}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }, body: fd }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
      const newDoc = await res.json();
      setKycDocuments(p => [...p, newDoc]);
      setNewDocName(''); setNewDocFile(null);
      toast({ title: "Success", description: "Document uploaded" });
    } catch (e) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
    finally { setUploadingDoc(false); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await fetch(`${backendUrl}/api/drivers/${driverId}/kyc-documents/${docId}`, { method: 'DELETE', headers: getAuthHeaders() });
      setKycDocuments(p => p.filter(d => d.id !== docId));
      toast({ title: "Deleted" });
    } catch { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
  };

  const handleDownloadDoc = (doc) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${backendUrl}/api/drive/files/${doc.drive_file_id}/download?token=${token}`, '_blank');
  };

  // ---- Validate + Save ----
  const handleSave = async () => {
    if (!form.vendor_id) { toast({ title: "Validation Error", description: "Vendor is required", variant: "destructive" }); return; }
    if (!form.name.trim()) { toast({ title: "Validation Error", description: "Name is required", variant: "destructive" }); return; }
    if (!form.mobile_1.trim()) { toast({ title: "Validation Error", description: "Mobile 1 is required", variant: "destructive" }); return; }
    if (!form.dl_no.trim()) { toast({ title: "Validation Error", description: "DL No. is required", variant: "destructive" }); return; }
    const validBanks = bankRows.filter(b => b.bank_name.trim() && b.account_no.trim());
    try {
      setSaving(true);
      const payload = {
        ...form,
        bhattu_local: form.bhattu_local !== '' ? parseFloat(form.bhattu_local) : null,
        bhattu_outstation: form.bhattu_outstation !== '' ? parseFloat(form.bhattu_outstation) : null,
        conductor_bhattu: form.conductor_bhattu !== '' ? parseFloat(form.conductor_bhattu) : null,
        dl_expiry_date: form.dl_expiry_date || null,
        date_of_joining: form.date_of_joining || null,
        relieving_date: form.relieving_date || null,
        bank_details: validBanks.map(b => ({ bank_name: b.bank_name, account_no: b.account_no, branch: b.branch || null, ifsc_code: b.ifsc_code || null }))
      };
      const res = await fetch(
        isEdit ? `${backendUrl}/api/drivers/${driverId}` : `${backendUrl}/api/drivers`,
        { method: isEdit ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to save'); }
      const saved = await res.json();
      toast({ title: "Success", description: isEdit ? "Driver updated" : "Driver created" });
      if (!isEdit) navigate(`/fleet/drivers/edit/${saved.id}`);
    } catch (e) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const indianStates = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh","Puducherry","Chandigarh"];

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary cursor-pointer hover:text-accent-blue" onClick={() => navigate('/fleet/drivers')}>Drivers</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-accent-blue font-medium">{isEdit ? 'Edit Driver' : 'Add Driver'}</span>
      </div>
      <h1 className="text-3xl font-bold text-text-dark font-manrope">{isEdit ? 'Edit Driver' : 'Add Driver'}</h1>

      {/* BASIC DETAILS */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Basic Details</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Vendor */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Vendor <span className="text-accent-red">*</span></Label>
            <Select value={form.vendor_id} onValueChange={v => set('vendor_id', v)}>
              <SelectTrigger data-testid="driver-vendor-select" className="border-gray-200 font-ibm"><SelectValue placeholder="Select Vendor" /></SelectTrigger>
              <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.business_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Name <span className="text-accent-red">*</span></Label>
            <Input data-testid="driver-name-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full Name" className="border-gray-200 font-ibm" />
          </div>
          {/* Aadhar */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Aadhar No.</Label>
            <Input data-testid="driver-aadhar-input" value={form.aadhar_no} onChange={e => set('aadhar_no', e.target.value.replace(/\D/g,''))} maxLength={12} placeholder="12-digit Aadhar" className="border-gray-200 font-ibm" />
          </div>
          {/* DL No */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">DL No. <span className="text-accent-red">*</span></Label>
            <Input data-testid="driver-dl-input" value={form.dl_no} onChange={e => set('dl_no', e.target.value.toUpperCase())} placeholder="DL Number" className="border-gray-200 font-ibm uppercase" />
          </div>
          {/* DL State */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">DL Issued By State</Label>
            <Select value={form.dl_issued_by_state} onValueChange={v => set('dl_issued_by_state', v)}>
              <SelectTrigger className="border-gray-200 font-ibm"><SelectValue placeholder="Select State" /></SelectTrigger>
              <SelectContent>{indianStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {/* DL Expiry */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">DL Expiry Date</Label>
            <Input data-testid="driver-dl-expiry" type="date" value={form.dl_expiry_date} onChange={e => set('dl_expiry_date', e.target.value)} className="border-gray-200 font-ibm" />
          </div>
          {/* Mobile 1 */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Mobile 1 <span className="text-accent-red">*</span></Label>
            <Input data-testid="driver-mobile1-input" type="tel" value={form.mobile_1} onChange={e => set('mobile_1', e.target.value.replace(/\D/g,''))} maxLength={10} placeholder="10-digit mobile" className="border-gray-200 font-ibm" />
          </div>
          {/* Mobile 2 */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Mobile 2</Label>
            <Input type="tel" value={form.mobile_2} onChange={e => set('mobile_2', e.target.value.replace(/\D/g,''))} maxLength={10} className="border-gray-200 font-ibm" />
          </div>
          {/* Bhattu fields */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Bhattu Local (₹)</Label>
            <Input type="number" value={form.bhattu_local} onChange={e => set('bhattu_local', e.target.value)} placeholder="0.00" className="border-gray-200 font-ibm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Bhattu Outstation (₹)</Label>
            <Input type="number" value={form.bhattu_outstation} onChange={e => set('bhattu_outstation', e.target.value)} placeholder="0.00" className="border-gray-200 font-ibm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Conductor Bhattu (₹)</Label>
            <Input type="number" value={form.conductor_bhattu} onChange={e => set('conductor_bhattu', e.target.value)} placeholder="0.00" className="border-gray-200 font-ibm" />
          </div>
          {/* Dates */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Date of Joining</Label>
            <Input type="date" value={form.date_of_joining} onChange={e => set('date_of_joining', e.target.value)} className="border-gray-200 font-ibm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium font-ibm text-text-dark">Relieving Date</Label>
            <Input type="date" value={form.relieving_date} onChange={e => set('relieving_date', e.target.value)} className="border-gray-200 font-ibm" />
          </div>
          {/* Address */}
          <div className="space-y-1.5 col-span-2">
            <Label className="text-sm font-medium font-ibm text-text-dark">Address</Label>
            <Textarea value={form.address} onChange={e => set('address', e.target.value)} className="border-gray-200 font-ibm min-h-[80px]" />
          </div>
        </div>
      </Card>

      {/* BANK DETAILS */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Bank Details</h2>
          <Button data-testid="add-bank-row-btn" type="button" onClick={addBankRow}
            className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-medium h-9 px-4 rounded-lg text-sm">
            <Plus className="w-4 h-4 mr-1.5" />Add Bank
          </Button>
        </div>
        <div className="p-6 space-y-3">
          {bankRows.map((row, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg relative">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium font-ibm text-text-dark">Bank Name</Label>
                <Input data-testid={`bank-name-${i}`} value={row.bank_name} onChange={e => setBankField(i, 'bank_name', e.target.value)} placeholder="Bank Name" className="border-gray-200 font-ibm h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium font-ibm text-text-dark">Account No.</Label>
                <Input data-testid={`bank-account-${i}`} value={row.account_no} onChange={e => setBankField(i, 'account_no', e.target.value)} placeholder="Account Number" className="border-gray-200 font-ibm h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium font-ibm text-text-dark">Branch</Label>
                <Input value={row.branch} onChange={e => setBankField(i, 'branch', e.target.value)} placeholder="Branch" className="border-gray-200 font-ibm h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium font-ibm text-text-dark">IFSC Code</Label>
                <div className="flex gap-2">
                  <Input value={row.ifsc_code} onChange={e => setBankField(i, 'ifsc_code', e.target.value.toUpperCase())} placeholder="IFSC" className="border-gray-200 font-ibm h-9 text-sm uppercase" />
                  {bankRows.length > 1 && (
                    <button type="button" onClick={() => removeBankRow(i)} className="w-9 h-9 flex items-center justify-center hover:bg-accent-red/10 rounded-lg shrink-0">
                      <Trash2 className="w-4 h-4 text-accent-red" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* KYC DOCUMENTS */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">KYC Documents</h2>
        </div>
        <div className="p-6">
          {!driveConnected && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              Google Drive is not connected. Connect Drive in Settings to upload KYC documents.
            </div>
          )}
          {/* Existing docs */}
          {kycDocuments.length > 0 && (
            <div className="mb-5 space-y-2">
              {kycDocuments.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-text-dark font-ibm">{doc.document_name}</p>
                    <p className="text-xs text-text-secondary font-ibm">{doc.file_name}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => handleDownloadDoc(doc)}>
                      <Download className="w-3.5 h-3.5 mr-1" />Download
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-accent-red hover:text-accent-red">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Upload new */}
          {isEdit && driveConnected ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Document Name</Label>
                <Input data-testid="kyc-doc-name-input" placeholder="e.g. Aadhar, DL Copy" value={newDocName} onChange={e => setNewDocName(e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Document File</Label>
                <Input type="file" data-testid="kyc-doc-file-input" onChange={e => setNewDocFile(e.target.files[0])} className="border-gray-200 font-ibm" />
              </div>
              <div className="flex items-end">
                <Button data-testid="kyc-upload-btn" type="button" onClick={handleUploadDoc} disabled={uploadingDoc || !newDocName || !newDocFile}
                  className="w-full bg-accent-orange hover:bg-accent-orange/90 text-white font-ibm font-semibold h-10 rounded-lg" style={{ backgroundColor: '#f59e0b' }}>
                  {uploadingDoc ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
                </Button>
              </div>
            </div>
          ) : !isEdit ? (
            <p className="text-sm text-text-secondary italic font-ibm">Save driver first to upload KYC documents.</p>
          ) : null}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <Button variant="outline" onClick={() => navigate('/fleet/drivers')} className="font-ibm font-semibold h-11 px-8">
          <X className="w-4 h-4 mr-2" />Cancel
        </Button>
        <Button data-testid="save-driver-btn" onClick={handleSave} disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-8">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Update Driver' : 'Save Driver'}</>}
        </Button>
      </div>
    </div>
  );
};

export default AddDriver;
